/**
 * Browser-side image resizer.
 *
 * Phone photos are routinely 4–8 MB which would (a) time out on upload, and
 * (b) bloat object storage. We downscale to a max long-edge of 1600px and
 * re-encode as JPEG q=0.85 — visually indistinguishable at vehicle-card sizes
 * but typically <250 KB.
 *
 * Returns a Blob you can hand straight to `FormData`.
 */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/**
 * Load an HTMLImageElement from a File or Blob.
 */
const loadImage = (file: File | Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode the image — try a different file."));
    };
    img.src = url;
  });

export interface ResizeResult {
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
}

/**
 * Resize + re-encode an image File to JPEG. Returns the resized Blob plus
 * the final dimensions for UI feedback.
 */
export async function resizeImage(file: File | Blob): Promise<ResizeResult> {
  const img = await loadImage(file);
  let { naturalWidth: w, naturalHeight: h } = img;

  // Compute the longest edge scale factor.
  const longEdge = Math.max(w, h);
  if (longEdge > MAX_EDGE) {
    const scale = MAX_EDGE / longEdge;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported in this browser");
  ctx.drawImage(img, 0, 0, w, h);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not encode the image"))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
  return { blob, width: w, height: h, bytes: blob.size };
}

/**
 * Detect if the device probably has a back-facing camera worth offering
 * a "Take a photo" button for. We use UA sniffing (good enough for the
 * camera-vs-laptop signal — both iOS Safari and Android Chrome match).
 */
export function isMobileLikely(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}
